// Lightweight client-side tracking of how the user moves through the app.
// Pure localStorage — no DB rows, no API calls. Used by DarumaFab to
// personalize proactive suggestions and to gamify help discovery.

const DIARY_KEY = 'tg_daruma_diary';
const HELP_SEEN_KEY = 'tg_daruma_help_seen';

interface DarumaDiary {
  pageVisits: Record<string, number>; // base path → visit count
  lastVisit: string;                  // YYYY-MM-DD of the last recorded visit
  sessionsCount: number;              // distinct days the app has been opened
  lastProactivePage: string | null;   // reserved for future use
}

const DEFAULT_DIARY: DarumaDiary = {
  pageVisits: {},
  lastVisit: '',
  sessionsCount: 0,
  lastProactivePage: null,
};

function getDiary(): DarumaDiary {
  try {
    const raw = localStorage.getItem(DIARY_KEY);
    if (raw) return { ...DEFAULT_DIARY, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_DIARY };
}

function saveDiary(d: DarumaDiary): void {
  try {
    localStorage.setItem(DIARY_KEY, JSON.stringify(d));
  } catch {
    /* ignore */
  }
}

export function recordPageVisit(path: string): void {
  const diary = getDiary();
  const basePath = '/' + (path.split('/')[1] || '');
  diary.pageVisits[basePath] = (diary.pageVisits[basePath] || 0) + 1;

  const today = new Date().toISOString().split('T')[0];
  if (diary.lastVisit !== today) {
    diary.sessionsCount++;
    diary.lastVisit = today;
  }
  saveDiary(diary);
}

export function getSessionsCount(): number {
  return getDiary().sessionsCount;
}

export type DarumaPose = 'wave' | 'point' | 'think' | 'explain' | 'celebrate' | 'travel';

export interface SmartSuggestion {
  messageKey: string;
  pose: DarumaPose;
  actionRoute?: string;
}

// Returns a personalized suggestion based on long-term behaviour, or null
// if the user hasn't earned enough signal yet. Each branch fires once via
// pageVisits flag rows (keys prefixed with `_`).
export function getSmartSuggestion(): SmartSuggestion | null {
  const diary = getDiary();
  const sessions = diary.sessionsCount;

  // After 3+ sessions, if user never opened Credits, nudge them.
  if (
    sessions >= 3 &&
    !diary.pageVisits['_nudgedCredits'] &&
    (!diary.pageVisits['/credits'] || diary.pageVisits['/credits'] < 1)
  ) {
    diary.pageVisits['_nudgedCredits'] = 1;
    saveDiary(diary);
    return {
      messageKey: 'daruma.diaryTryCredits',
      pose: 'point',
      actionRoute: '/credits',
    };
  }

  // Heavy Dream visitor with no Travel activity → suggest planning.
  const dreamVisits = diary.pageVisits['/dream'] || 0;
  const travelVisits = diary.pageVisits['/travel'] || 0;
  if (
    dreamVisits >= 5 &&
    travelVisits < 2 &&
    !diary.pageVisits['_nudgedTravel']
  ) {
    diary.pageVisits['_nudgedTravel'] = 1;
    saveDiary(diary);
    return {
      messageKey: 'daruma.diaryExplorerNoTrips',
      pose: 'travel',
      actionRoute: '/travel',
    };
  }

  // Loyalty thank-you at 10 sessions, exactly once.
  if (sessions >= 10 && !diary.pageVisits['_thanked10']) {
    diary.pageVisits['_thanked10'] = 1;
    saveDiary(diary);
    return {
      messageKey: 'daruma.diary10Sessions',
      pose: 'celebrate',
    };
  }

  return null;
}

// ─── Help discovery progress ───
// A page is "explored" once the user opens DarumaFab help mode for it.

export function getHelpSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HELP_SEEN_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

export function markHelpSeen(pageKey: string): void {
  const seen = getHelpSeen();
  seen.add(pageKey);
  try {
    localStorage.setItem(HELP_SEEN_KEY, JSON.stringify([...seen]));
  } catch {
    /* ignore */
  }
}

const HELP_PAGES = ['/', '/dream', '/trip-planner', '/travel', '/share', '/credits', '/profile', '/achievements'];

export function getHelpProgress(): { seen: number; total: number; percentage: number } {
  const seen = getHelpSeen();
  const total = HELP_PAGES.length;
  // Only count pages that are part of HELP_PAGES — old keys don't pollute progress.
  const seenCount = HELP_PAGES.filter(p => seen.has(p)).length;
  return {
    seen: seenCount,
    total,
    percentage: Math.round((seenCount / total) * 100),
  };
}
